import React, { useEffect, useState } from "react";
import { getWorkload, updateWorkloadSettings } from "../../../api";
import { useToast } from "../../../hooks/use-toast";
import { Gauge, AlertTriangle, Loader2, CalendarOff } from "lucide-react";
import { log } from "../../../lib/log";

const pad = (n) => String(n).padStart(2, "0");
export const isoWeek = (d) => {
  const x = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const day = x.getUTCDay() || 7;
  x.setUTCDate(x.getUTCDate() + 4 - day);
  const y = x.getUTCFullYear();
  const week = Math.ceil(((x - Date.UTC(y, 0, 1)) / 86400000 + 1) / 7);
  return `${y}-W${pad(week)}`;
};
const DAYS = ["L", "M", "M", "J", "V", "S", "D"];
const hm = (iso) => String(iso).slice(11, 16);
const dm = (iso) => new Date(iso).toLocaleDateString("fr-FR", { weekday: "short", day: "numeric" });

const barColor = (m) => {
  if (m.overlaps.length) return "bg-red-500";
  return m.over_threshold ? "bg-amber-500" : "bg-emerald-500";
};

const DayCell = ({ h, abs, label }) => {
  if (abs) return <span title="Absent" className="w-5 h-5 rounded text-[10px] flex items-center justify-center bg-red-100 text-red-600 font-bold">✕</span>;
  const cls = h ? "bg-brand/10 text-brand font-semibold" : "bg-neutral-50 text-neutral-300";
  return <span title={`${h} h`} className={`w-5 h-5 rounded text-[10px] flex items-center justify-center ${cls}`}>{h ? Math.round(h) : label}</span>;
};

const MemberRow = ({ m, threshold, dates = [] }) => {
  const seuil = m.threshold_h || threshold;
  const pct = Math.min(100, (m.hours / seuil) * 100);
  const color = barColor(m);
  const absentDates = new Set((m.absent_days || []).map((a) => a.date));
  return (
    <div className="py-2 border-b border-neutral-100 last:border-0" data-testid={`workload-row-${m.login}`}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
        <span className="font-semibold text-neutral-800 min-w-[130px] truncate">{m.nom}{m.level === "major" ? <span className="text-[10px] text-neutral-400 ml-1">major</span> : null}</span>
        <div className="flex-1 min-w-[140px] h-2.5 bg-neutral-100 rounded-full overflow-hidden"><div className={`h-full rounded-full transition-[width] duration-500 ${color}`} style={{ width: `${pct}%` }} /></div>
        <span className={`text-xs font-semibold tabular-nums ${m.over_threshold ? "text-amber-600" : "text-neutral-600"}`} data-testid={`workload-hours-${m.login}`} title={m.weekly_hours ? "Seuil = heures hebdo contractuelles (Dolibarr)" : "Seuil global"}>
          {m.hours} h / {seuil} h{m.weekly_hours ? " (contrat)" : ""} · {m.count} interv.{m.worked_hours ? ` · ${m.worked_hours} h pointées` : ""}
        </span>
        <span className="hidden md:flex gap-1">{m.per_day.map((h, i) => {
          const iso = dates[i];
          const abs = iso && absentDates.has(iso);
          return <DayCell key={DAYS[i]} h={h} abs={abs} label={DAYS[i]} />;
        })}</span>
        {m.absent_days?.length > 0 && <span className="flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700" data-testid={`workload-absent-${m.login}`}><CalendarOff size={12} /> absent {m.absent_days.length} j</span>}
        {m.overlaps.length > 0 && <span className="flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700" data-testid={`workload-overlap-${m.login}`}><AlertTriangle size={12} /> {m.overlaps.length} chevauchement(s)</span>}
      </div>
      {m.overlaps.map((o) => (
        <p key={`${o.a.id || o.a.date}-${o.b.id || o.b.date}`} className="text-xs text-red-600 mt-1 ml-1">↳ {dm(o.a.date)} : « {o.a.client_nom} » {hm(o.a.date)} ({o.a.duree_h} h) ↔ « {o.b.client_nom} » {hm(o.b.date)} ({o.b.duree_h} h)</p>
      ))}
    </div>
  );
};

const WorkloadPanel = ({ weekStart, refreshKey, isMaster, serviceLogins = null }) => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(true);
  const { toast } = useToast();
  const week = isoWeek(weekStart);

  useEffect(() => {
    let alive = true;
    getWorkload(week).then((r) => alive && setData(r.data)).catch((e) => log.error("charge:", e));
    return () => { alive = false; };
  }, [week, refreshKey]);

  const changeThreshold = async (v) => {
    const th = parseFloat(v);
    if (!th) return;
    try { const r = await updateWorkloadSettings(th); setData((d) => ({ ...d, threshold_h: r.data.threshold_h, members: d.members.map((m) => ({ ...m, over_threshold: m.hours > r.data.threshold_h })) })); toast({ title: "Seuil de charge mis à jour ✓" }); }
    catch (e) { toast({ title: "Erreur", description: e?.response?.data?.detail || "Échec", variant: "destructive" }); }
  };

  const busy = data?.members.filter((m) => (m.count > 0 || m.absent_days?.length) && (!serviceLogins || serviceLogins.has(m.login))) || [];
  const dates = data ? Array.from({ length: 7 }, (_, i) => { const d = new Date(data.start + "T12:00"); d.setDate(d.getDate() + i); return d.toISOString().slice(0, 10); }) : [];
  return (
    <div className="bg-white border border-neutral-200 rounded-xl mb-4" data-testid="workload-panel">
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5">
        <button onClick={() => setOpen(!open)} className="flex items-center gap-2 text-sm font-semibold text-neutral-800" data-testid="workload-toggle">
          <Gauge size={16} className="text-brand" /> Charge hebdomadaire par easer
          {data ? <span className="text-xs text-neutral-400 font-normal">· semaine {data.week.split("-W")[1]} · {busy.length} easer(s) planifié(s)</span> : <Loader2 size={13} className="animate-spin text-neutral-400" />}
        </button>
        <div className="flex items-center gap-3">
          {data?.total_overlaps > 0 && <span className="flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full bg-red-100 text-red-700" data-testid="workload-total-overlaps"><AlertTriangle size={13} /> {data.total_overlaps} chevauchement(s)</span>}
          {data && (
            <label className="text-xs text-neutral-500 flex items-center gap-1">Seuil
              <input type="number" min={1} max={80} step={0.5} defaultValue={data.threshold_h} key={data.threshold_h} disabled={!isMaster} data-testid="workload-threshold" onBlur={(e) => Number(e.target.value) !== data.threshold_h && changeThreshold(e.target.value)} className="w-14 border border-neutral-300 rounded px-1.5 py-0.5 text-xs disabled:bg-neutral-50" /> h
            </label>
          )}
        </div>
      </div>
      {open && data && (
        <div className="px-4 pb-3">
          {busy.length === 0 ? <p className="text-xs text-neutral-400 py-1">Aucune intervention planifiée cette semaine.</p>
            : busy.map((m) => <MemberRow key={m.login || m.nom} m={m} threshold={data.threshold_h} dates={dates} />)}
        </div>
      )}
    </div>
  );
};

export default WorkloadPanel;
